import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, peso, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { STATUS_LABELS, STATUS_COLORS, fmtAddress } from "@/components/OrderTicket";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { LogOut, Truck, Store, Volume2, VolumeX, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";

const COLUMNS = [
  { key: "received", next: "confirmed" },
  { key: "confirmed", next: "preparing" },
  { key: "preparing", next: "packing" },
  { key: "packing", next: "ready" },
  { key: "ready", next: "out_for_delivery" },
  { key: "out_for_delivery", next: "delivered" },
  { key: "delivered", next: "completed" },
  { key: "completed", next: null },
];

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = 880; o.type = "sine"; g.gain.value = 0.15;
    o.start(); o.stop(ctx.currentTime + 0.25);
  } catch (_) {}
}

export default function StoreDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [sound, setSound] = useState(true);
  const prevNew = useRef(0);
  const soundRef = useRef(sound);
  soundRef.current = sound;

  const load = useCallback(() => {
    api.get("/orders").then((r) => {
      const data = r.data;
      const newCount = data.filter((o) => o.status === "received").length;
      if (newCount > prevNew.current && soundRef.current) { beep(); toast.info("🔔 New order received!"); }
      prevNew.current = newCount;
      setOrders(data);
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 7000); return () => clearInterval(t); }, [load]);

  const advance = async (o, status, reason = "") => {
    try {
      await api.put(`/orders/${o.id}/status`, { status, reason });
      toast.success(`${o.order_number} → ${STATUS_LABELS[status]}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const active = orders.filter((o) => !["completed", "cancelled", "refunded", "failed"].includes(o.status));
  const newCount = orders.filter((o) => o.status === "received").length;

  return (
    <div className="min-h-screen bg-[#F4F6F4]">
      <header className="bg-[#0E3A14] text-white sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4"><BrandLogo size="sm" light /><Badge className="bg-white/10">Store Ops · {user.name}</Badge></div>
          <div className="flex items-center gap-3">
            {newCount > 0 && <Badge className="bg-[#C62828] as-alert" data-testid="new-order-counter">{newCount} NEW</Badge>}
            <button onClick={() => setSound((s) => !s)} className="text-white/80" data-testid="sound-toggle">{sound ? <Volume2 size={20} /> : <VolumeX size={20} />}</button>
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={async () => { await logout(); nav("/"); }} data-testid="store-logout"><LogOut size={16} /></Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto p-4">
        <div className="flex gap-4 overflow-x-auto pb-4">
          {COLUMNS.map((col) => {
            const list = active.filter((o) => o.status === col.key).concat(col.key === "completed" ? orders.filter((o) => o.status === "completed").slice(0, 10) : []);
            return (
              <div key={col.key} className="min-w-[300px] w-[300px] shrink-0" data-testid={`kanban-col-${col.key}`}>
                <div className="flex items-center justify-between mb-2 px-1">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full" style={{ background: STATUS_COLORS[col.key] }} />
                    <h3 className="font-semibold text-sm">{STATUS_LABELS[col.key]}</h3>
                  </div>
                  <Badge variant="secondary">{list.length}</Badge>
                </div>
                <div className="space-y-3">
                  {list.map((o) => (
                    <div key={o.id} className={`bg-white rounded-xl border p-3 shadow-sm ${o.status === "received" ? "ring-2 ring-[#C62828]/40" : ""}`} data-testid={`order-card-${o.order_number}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-display font-bold text-sm text-[#1B5E20]">{o.order_number}</span>
                        <Badge className="text-[10px]" variant="outline">{o.order_type === "delivery" ? <Truck size={12} /> : <Store size={12} />}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                      <div className="mt-2 text-sm font-medium">{o.contact_name}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1"><Phone size={11} /> {o.contact_phone}</div>
                      {o.address && <div className="text-xs text-muted-foreground flex items-start gap-1 mt-0.5"><MapPin size={11} className="mt-0.5 shrink-0" /> {fmtAddress(o.address)}</div>}
                      <div className="mt-2 border-t pt-2 space-y-0.5">
                        {o.items.map((i, idx) => <div key={idx} className="text-xs flex justify-between"><span>{i.quantity}× {i.name}</span><span>{peso(i.line_total)}</span></div>)}
                      </div>
                      {o.delivery_notes && <div className="text-xs mt-1 text-[#C62828]">📝 {o.delivery_notes}</div>}
                      <div className="flex justify-between items-center mt-2 pt-2 border-t">
                        <span className="text-xs uppercase font-semibold">{o.payment_method}</span>
                        <span className="font-bold text-[#1B5E20]">{peso(o.grand_total)}</span>
                      </div>
                      {col.next && (
                        <div className="flex gap-2 mt-2">
                          {["received", "confirmed"].includes(o.status) && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="outline" className="flex-1 text-[#C62828] border-[#C62828]/30" data-testid={`cancel-${o.order_number}`}>Cancel</Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader><AlertDialogTitle>Cancel {o.order_number}?</AlertDialogTitle><AlertDialogDescription>This will mark the order as cancelled. This action is logged.</AlertDialogDescription></AlertDialogHeader>
                                <AlertDialogFooter><AlertDialogCancel>Keep</AlertDialogCancel><AlertDialogAction className="bg-[#C62828]" onClick={() => advance(o, "cancelled", "Cancelled by store")} data-testid={`confirm-cancel-${o.order_number}`}>Cancel Order</AlertDialogAction></AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                          <Button size="sm" className="flex-1 bg-[#1B5E20] hover:bg-[#144718]" onClick={() => advance(o, col.next)} data-testid={`advance-${o.order_number}`}>
                            {STATUS_LABELS[col.next].split(" ")[0]} →
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                  {list.length === 0 && <div className="text-xs text-muted-foreground text-center py-6 border-2 border-dashed rounded-xl">No orders</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
